import { Head, Link } from '@inertiajs/react'
import { HiArrowCircleRight } from 'react-icons/hi'
import { HiOutlineCalendar, HiOutlineMapPin, HiOutlinePlus } from 'react-icons/hi2'
import AppLayout from '../components/AppLayout'

type EventStatus = 'upcoming' | 'ongoing' | 'completed'

type EventCard = {
  id: number
  title: string
  slug: string
  location: string
  date_range: string
  status: EventStatus
  banner_url: string | null
  leads_count: number
}

type Props = {
  events: EventCard[]
  is_manager: boolean
}

export default function Dashboard({ events, is_manager }: Props) {
  return (
    <>
      <Head title="Dashboard" />
      <AppLayout active="dashboard">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink md:text-[40px] md:leading-[1.2]">
            Dashboard
          </h1>
          {is_manager && (
            <Link
              href="/events/new"
              className="inline-flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 font-display text-sm font-medium text-white transition hover:bg-midnight-green"
            >
              <HiOutlinePlus className="h-4 w-4" aria-hidden="true" />
              Create Event
            </Link>
          )}
        </div>

        <section className="mt-8">
          <h2 className="font-display text-base font-medium text-black/60">
            Your Events ({events.length})
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <EventCardItem key={event.id} event={event} />
            ))}
          </div>
        </section>
      </AppLayout>
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// EventCardItem — kept inline (single caller) per the project rule.
// Extract to components/ once a second page renders these cards.
// ────────────────────────────────────────────────────────────────

function EventCardItem({ event }: { event: EventCard }) {
  return (
    <article className="flex flex-col overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
      <div className="relative h-[150px] w-full bg-gray-100">
        {event.banner_url ? (
          <img
            src={event.banner_url}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : null}
        <StatusBadge status={event.status} />
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="font-display text-xl font-semibold tracking-tight text-midnight-green">
          {event.title}
        </h3>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1 font-display text-sm text-black/60">
            <HiOutlineCalendar className="h-4 w-4" aria-hidden="true" />
            {event.date_range}
          </span>
          <span className="inline-flex items-center gap-1 font-display text-sm text-gray-600">
            <HiOutlineMapPin className="h-4 w-4" aria-hidden="true" />
            {event.location}
          </span>
        </div>

        <div className="mt-2 border-t border-gray-200" />

        <div className="mt-auto flex items-center justify-between pt-1">
          <p className="font-display text-ink">
            <span className="text-lg font-medium">{event.leads_count}</span>{' '}
            <span className="text-base font-medium">leads</span>
          </p>
          <Link
            href={`/events/${event.slug}/analytics`}
            className="inline-flex items-center gap-2 rounded-md bg-ink px-6 py-3 font-display text-base font-medium text-white transition hover:bg-midnight-green"
          >
            Dashboard
            <HiArrowCircleRight className="h-5 w-5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  )
}

function StatusBadge({ status }: { status: EventStatus }) {
  const styles: Record<EventStatus, string> = {
    upcoming: 'bg-white text-ink',
    ongoing: 'bg-[#93D500] text-ink',
    completed: 'bg-ink text-white',
  }
  const label = status.charAt(0).toUpperCase() + status.slice(1)
  return (
    <span
      className={`absolute right-3 top-3 rounded-[10px] border border-white px-2.5 py-0.5 font-display text-xs font-medium ${styles[status]}`}
    >
      {label}
    </span>
  )
}
